import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  Archive,
  ArchiveRestore,
  BookOpen,
  Calendar,
  ClipboardCheck,
  ExternalLink,
  Mail,
  MapPin,
  MessageCircle,
  MoreVertical,
  Pencil,
  Phone,
  Plus,
  Save,
  Trash2,
  User,
  Users,
  Video,
} from 'lucide-react'
import { Badge, Button, Card } from '@/components/ui'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { PedagogyBackLink } from '@/components/pedagogy/PedagogyBackLink'
import {
  TeacherLifecyclePanel,
  type TeacherLifecyclePanelHandle,
} from '@/components/pedagogy/TeacherLifecyclePanel'
import { PedagogyTeacherCreatePage } from '@/pages/PedagogyTeacherCreatePage'
import {
  AttestationResultBadge,
  TeacherStatusBadge,
  teacherStatusLabels,
  TrainingStatusBadge,
} from '@/components/pedagogy/TeacherStatusBadge'
import { PedagogyAccessErrorCard } from '@/components/pedagogy/PedagogyAccessStates'
import { usePedagogyAccess } from '@/hooks/usePedagogyAccess'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import { getTeacherInlineAttention, teacherAttentionClass } from '@/lib/teacherAttention'
import {
  addTeacherAdditionalAttestation,
  archiveFranchiseTeacher,
  addTeacherVideoPresentation,
  getFranchiseTeacherDetail,
  listTeacherRegistryReferenceData,
  overrideUsedTeacherVideoDecision,
  restoreArchivedFranchiseTeacher,
  updateFranchiseTeacher,
  updateFranchiseTeacherContract,
  updateTeacherAdditionalAttestation,
  updateTeacherVideoPresentation,
  type AdditionalAttestationType,
  type FranchiseTeacherDetail,
  type TeacherAdditionalAttestation,
  type TeacherContract,
  type TeacherQualification,
  type TeacherRegistryReferenceData,
  type TeacherTrainingAttestationAttempt,
  type TeacherTrainingCycle,
  type TeacherVideoDecision,
  type TeacherVideoPresentation,
} from '@/services/franchiseTeacherService'
import { confirmTeacherFranchiseAssignment, confirmTeacherRegistryCard } from '@/services/teacherAppAccessService'
import { listTrainerReviewDirections, type TrainerReviewDirectionOption } from '@/services/trainerReviewService'

const qualificationLabels: Record<TeacherQualification, string> = {
  stretch_pilates: 'Растяжка и пилатес',
  universal: 'Универсал',
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const additionalTypeLabels: Record<AdditionalAttestationType, string> = {
  regulation: 'Аттестация по регламенту',
  low_quality: 'Аттестация из-за низкого качества',
  planned_recertification: 'Плановая переаттестация',
}

type DetailTab = 'profile' | 'training'
type VideoDecisionDraft = '' | TeacherVideoDecision
type TeacherAssignment = FranchiseTeacherDetail['assignments'][number]

const detailTabs: Array<{ value: DetailTab; label: string; icon: typeof User }> = [
  { value: 'profile', label: 'Профиль', icon: User },
  { value: 'training', label: 'Обучение', icon: BookOpen },
]

function localIsoDate(): string {
  const now = new Date()
  const offset = now.getTimezoneOffset() * 60_000
  return new Date(now.getTime() - offset).toISOString().slice(0, 10)
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const normalized = value.length > 10 ? value : `${value}T00:00:00`
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('ru-RU').format(date)
}

function formatTelegramUsername(value: string): string {
  const normalized = value.trim().replace(/^@+/, '')
  return normalized ? `@${normalized}` : 'Telegram не указан'
}

function normalizeTelegramUsername(value: string): string {
  return value.trim().replace(/^@+/, '')
}

function trainingEndLabel(cycle: TeacherTrainingCycle): string {
  const finishedAt = cycle.completedAt ?? cycle.closedAt
  if (finishedAt) return formatDate(finishedAt)
  if (cycle.status === 'in_progress') return 'по настоящее время'
  return cycle.status === 'completed' ? 'дата завершения не указана' : 'дата закрытия не указана'
}


function videoDecisionLabel(decision: TeacherVideoDecision | null): string {
  if (decision === 'approved') return 'Допущен'
  if (decision === 'rejected') return 'Не допущен'
  return 'Ждёт оценки'
}

function videoDecisionVariant(decision: TeacherVideoDecision | null): 'success' | 'error' | 'warning' {
  if (decision === 'approved') return 'success'
  if (decision === 'rejected') return 'error'
  return 'warning'
}

function isOverdue(value: string | null): boolean {
  if (!value) return false
  const date = new Date(`${value}T23:59:59`)
  return !Number.isNaN(date.getTime()) && date.getTime() < Date.now()
}

function daysOverdue(value: string | null): number {
  if (!value) return 0
  const deadline = new Date(`${value}T23:59:59`)
  if (Number.isNaN(deadline.getTime())) return 0
  const diff = Date.now() - deadline.getTime()
  return diff > 0 ? Math.max(1, Math.ceil(diff / 86_400_000)) : 0
}

function SectionHeader({
  icon: Icon,
  title,
  count,
  action,
}: {
  icon: typeof User
  title: string
  count?: number
  action?: ReactNode
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-primary" />
        <h2 className="font-semibold">{title}</h2>
        {count != null && <Badge variant="neutral">{count}</Badge>}
      </div>
      {action}
    </div>
  )
}

function EmptyState({
  text,
  actionLabel,
  onAction,
}: {
  text: string
  actionLabel?: string
  onAction?: () => void
}) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-muted/30 px-4 py-5 text-center">
      <div className="text-sm text-muted-foreground">{text}</div>
      {actionLabel && onAction && (
        <Button size="sm" variant="outline" className="mt-3" onClick={onAction}>
          <Plus size={15} className="mr-1.5 inline" />
          {actionLabel}
        </Button>
      )}
    </div>
  )
}


function formatQuality(value: number | null): string {
  return value == null ? '—' : `${value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`
}

function pluralRu(value: number, one: string, few: string, many: string): string {
  const mod10 = Math.abs(value) % 10
  const mod100 = Math.abs(value) % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

function contractStatusLabel(contract: TeacherContract): string {
  if (contract.status === 'active' && isOverdue(contract.expiresOn)) return 'нужно обновить'
  if (contract.status === 'active') return 'действует'
  if (contract.status === 'replaced') return 'заменён'
  return 'расторгнут'
}

function contractStatusVariant(contract: TeacherContract): 'success' | 'warning' | 'error' | 'neutral' {
  if (contract.status === 'active' && isOverdue(contract.expiresOn)) return 'error'
  if (contract.status === 'active') return 'success'
  return 'neutral'
}

function trainingStageLabel(cycle: TeacherTrainingCycle): string {
  if (cycle.status === 'completed') return 'Завершение'
  if (cycle.status === 'closed') return 'Закрыто'
  if (cycle.thirdAttestationResult === 'passed') return 'Завершение'
  if (cycle.secondAttestationResult === 'passed') return '3-я аттестация'
  if (cycle.firstAttestationResult === 'passed') return '2-я аттестация'
  return '1-я аттестация'
}

function ContractExternalLink({ contract }: { contract: TeacherContract }) {
  if (!contract.contractUrl) {
    return <span className="text-sm text-muted-foreground">Ссылка не указана</span>
  }

  return (
    <a href={contract.contractUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary">
      Открыть договор <ExternalLink size={13} />
    </a>
  )
}

function AttestationAttemptHistory({
  attempts,
  compact = false,
}: {
  attempts: TeacherTrainingAttestationAttempt[]
  compact?: boolean
}) {
  if (attempts.length === 0) return null

  const ordered = [...attempts].sort((left, right) => {
    const attemptDiff = left.attemptNo - right.attemptNo
    if (attemptDiff) return attemptDiff

    // Легаси-попытки приходят без даты (is_legacy_import), поэтому дата никогда не
    // разыменовывается напрямую: обе стороны нормализуются, известная дата идёт
    // раньше неизвестной, а равные даты доупорядочиваются по createdAt.
    const leftDate = left.attestationDate ?? ''
    const rightDate = right.attestationDate ?? ''
    if (leftDate !== rightDate) {
      if (!leftDate) return 1
      if (!rightDate) return -1
      return leftDate.localeCompare(rightDate)
    }

    return left.createdAt.localeCompare(right.createdAt)
  })

  return (
    <details className={compact ? 'mt-2 text-xs' : 'mt-2 rounded-lg border border-border/70 bg-background px-3 py-2'}>
      <summary className="cursor-pointer text-xs font-medium text-primary">
        {compact ? `История · ${ordered.length}` : `История попыток · ${ordered.length}`}
      </summary>
      <div className={compact ? 'mt-1.5 divide-y divide-border/70 border-t border-border/70' : 'mt-2 divide-y divide-border/70'}>
        {ordered.map(attempt => (
          <div key={attempt.attemptId} className={`flex items-center justify-between gap-3 ${compact ? 'py-1.5' : 'py-2 first:pt-0 last:pb-0'}`}>
            <div className="min-w-0">
              <div className="text-xs font-medium">Попытка {attempt.attemptNo}</div>
              <div className="text-xs text-muted-foreground">
                {attempt.attestationDate ? formatDate(attempt.attestationDate) : 'Дата неизвестна'}
              </div>
            </div>
            <AttestationResultBadge result={attempt.result} />
          </div>
        ))}
      </div>
    </details>
  )
}

function TrainingHistoryAttestation({
  title,
  date,
  result,
  attempts,
}: {
  title: string
  date: string | null
  result: TeacherTrainingCycle['firstAttestationResult']
  attempts: TeacherTrainingAttestationAttempt[]
}) {
  return (
    <div className="rounded-lg border border-border/70 px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-medium">{title}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {result == null && date ? `Запланировано: ${formatDate(date)}` : formatDate(date)}
          </div>
        </div>
        <AttestationResultBadge result={result} />
      </div>
      <AttestationAttemptHistory attempts={attempts} />
    </div>
  )
}

function AttestationCard({
  title,
  date,
  result,
  attempts,
  disabled,
  active,
  canEdit,
  showAction = true,
  onEdit,
}: {
  title: string
  date: string | null
  result: TeacherTrainingCycle['firstAttestationResult']
  attempts: TeacherTrainingAttestationAttempt[]
  disabled?: boolean
  active?: boolean
  canEdit?: boolean
  showAction?: boolean
  onEdit: () => void
}) {
  const actionLabel = result === 'passed'
    ? 'Исправить дату'
    : result === 'retake'
      ? 'Назначить пересдачу'
      : date
        ? 'Зафиксировать результат'
        : 'Запланировать'

  const dateLabel = result == null && date
    ? `Запланировано на ${formatDate(date)}`
    : date
      ? formatDate(date)
      : 'Дата не назначена'

  return (
    <div className={`rounded-lg border px-3 py-2.5 ${active ? 'border-primary/40 bg-primary/[0.02]' : 'border-border/70 bg-card'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-semibold">{title}</div>
            <AttestationResultBadge result={result} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span className="font-medium text-foreground">{dateLabel}</span>
            {attempts.length > 0 && (
              <span className="text-muted-foreground">
                {attempts.length} {attempts.length === 1 ? 'попытка' : attempts.length < 5 ? 'попытки' : 'попыток'}
              </span>
            )}
            {active && <span className="font-medium text-primary">Текущий этап</span>}
          </div>
        </div>
        {canEdit && showAction && (
          <details className="relative shrink-0">
            <summary className="inline-flex h-7 w-7 cursor-pointer list-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted" aria-label={`Действия: ${title}`}>
              <MoreVertical size={15} />
            </summary>
            <div className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-border bg-card p-1 shadow-lg">
              <button
                type="button"
                disabled={disabled}
                onClick={event => {
                  event.currentTarget.closest('details')?.removeAttribute('open')
                  onEdit()
                }}
                className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
              >
                {actionLabel}
              </button>
            </div>
          </details>
        )}
      </div>

      {attempts.length > 0 && <AttestationAttemptHistory attempts={attempts} compact />}
    </div>
  )
}

export function PedagogyTeacherDetailPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { teacherId } = useParams<{ teacherId: string }>()
  const lifecycleRef = useRef<TeacherLifecyclePanelHandle | null>(null)
  const isCreateRoute = teacherId === 'new'
  const hasValidTeacherId = Boolean(teacherId && UUID_PATTERN.test(teacherId))
  const {
    loading: accessLoading,
    canViewPedagogySection,
    canManagePedagogy,
    isPedagogySpecialist,
    accessError,
    retryAccessCheck,
  } = usePedagogyAccess()
  const [teacher, setTeacher] = useState<FranchiseTeacherDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<DetailTab>('profile')
  const [editOpen, setEditOpen] = useState(false)
  const [commentOpen, setCommentOpen] = useState(false)
  const [commentDraft, setCommentDraft] = useState('')
  const [commentExpanded, setCommentExpanded] = useState(false)
  const [videoOpen, setVideoOpen] = useState(false)
  const [editingVideoId, setEditingVideoId] = useState<string | null>(null)
  const [evaluateVideo, setEvaluateVideo] = useState(false)
  const [videoDecisionOverrideOpen, setVideoDecisionOverrideOpen] = useState(false)
  const [additionalOpen, setAdditionalOpen] = useState(false)
  const [editingAdditionalId, setEditingAdditionalId] = useState<string | null>(null)
  const [contractOpen, setContractOpen] = useState(false)
  const [editingContractId, setEditingContractId] = useState<string | null>(null)
  const [archiveMenuOpen, setArchiveMenuOpen] = useState(false)
  const archiveMenuRef = useRef<HTMLDivElement | null>(null)
  const preferredPartnerNumber = teacher?.assignments[0]?.partnerNumber ?? null
  /**
   * Архивация и восстановление жили на `window.confirm` + `window.prompt`.
   * `saveError` рендерится только внутри листов, поэтому «Укажите причину»
   * не показывалось никогда, а в Telegram WebView (в первую очередь iOS)
   * `prompt` бывает заблокирован и возвращает `null` без диалога — операция
   * становилась физически невыполнимой и молча.
   */
  const [archiveMode, setArchiveMode] = useState<null | 'archive' | 'restore'>(null)
  const [archiveReason, setArchiveReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [directionOptions, setDirectionOptions] = useState<TrainerReviewDirectionOption[]>([])
  const [assignmentReference, setAssignmentReference] = useState<TeacherRegistryReferenceData | null>(null)
  const [videoDraft, setVideoDraft] = useState({
    videoUrl: '', charismaScore: '', professionalismScore: '', clarityScore: '', emotionalityScore: '',
    decision: '' as VideoDecisionDraft, comment: '',
  })
  const [videoDecisionOverrideDraft, setVideoDecisionOverrideDraft] = useState({
    videoPresentationId: '',
    decision: '' as VideoDecisionDraft,
    reason: '',
  })
  const [additionalDraft, setAdditionalDraft] = useState({
    attestationType: 'planned_recertification' as AdditionalAttestationType,
    directionId: '', attestationDate: localIsoDate(), result: '' as '' | 'passed' | 'retake' | 'refused', comment: '',
  })
  const [contractDraft, setContractDraft] = useState({
    partnerNumber: '',
    signedOn: '',
    contractUrl: '',
  })
  const [draft, setDraft] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    telegramUsername: '',
    qualification: '' as '' | TeacherQualification,
  })

  const usedVideoIds = useMemo(() => new Set(
    (teacher?.trainingCycles ?? [])
      .map(cycle => cycle.approvedVideoPresentationId)
      .filter((id): id is string => Boolean(id))
  ), [teacher?.trainingCycles])
  const editingVideoIsUsed = editingVideoId ? usedVideoIds.has(editingVideoId) : false
  const canEditTeacher = Boolean(teacher?.canManage && !teacher.archivedAt)
  /**
   * Добавление связи гейтится НОВЫМ франчайзи, а не текущими связями педагога:
   * `add_franchise_teacher_assignment` спрашивает только
   * `pedagogy_actor_can_access_partner(p_partner_number)`. Поэтому кнопка живёт
   * на своей оси, а не под `canEditTeacher` (тот повторяет `can_manage`, который
   * у специалиста требует АКТИВНУЮ связь педагога с франчайзи его зоны).
   * Иначе перевод педагога к себе — единственная операция, которую сервер с
   * «чужим» педагогом разрешает, — из интерфейса недоступна: у уволенного
   * активных связей нет вовсе, у работающего они у чужого франчайзи.
   * Пока справочник не загрузился, действует прежнее правило — свои карточки
   * не должны ждать лишнего запроса.
   */
  const canAddAssignment = Boolean(
    teacher
    && !teacher.archivedAt
    && (canEditTeacher || (assignmentReference?.assignmentOptions.length ?? 0) > 0)
  )
  const anySheetOpen = editOpen || commentOpen || videoOpen || videoDecisionOverrideOpen
    || additionalOpen || contractOpen || archiveMode != null
  const commentNeedsExpansion = Boolean(teacher?.comment && (teacher.comment.length > 180 || teacher.comment.split('\n').length > 2))
  const backToTeachers = typeof location.state === 'object'
    && location.state != null
    && 'from' in location.state
    && typeof location.state.from === 'string'
    ? location.state.from
    : pedagogyPaths.teachers

  useEffect(() => {
    if (!archiveMenuOpen) return
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!archiveMenuRef.current?.contains(event.target as Node)) setArchiveMenuOpen(false)
    }
    document.addEventListener('pointerdown', closeOnOutsidePointer)
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer)
  }, [archiveMenuOpen])

  /**
   * `silent` — перечитать карточку, не разбирая экран. Любое сохранение здесь
   * заканчивается рефетчем, и без этого флага весь экран заменялся строкой
   * «Загружаем карточку…»: терялся скролл, закрывались раскрытые `<details>`
   * и меню действий, подтверждение успеха уничтожалось собственной перезагрузкой.
   */
  const loadTeacher = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true
    if (!teacherId || isCreateRoute) {
      setLoading(false)
      return
    }
    if (!hasValidTeacherId) {
      setTeacher(null)
      setError('Некорректный идентификатор педагога')
      setLoading(false)
      return
    }
    if (!silent) setLoading(true)
    setError('')
    try {
      const detail = await getFranchiseTeacherDetail(teacherId)
      setTeacher(detail)
      if (!detail) setError('Карточка педагога не найдена')
    } catch (loadError) {
      console.error('Failed to load teacher detail:', loadError)
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить карточку педагога')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [hasValidTeacherId, isCreateRoute, teacherId])

  useEffect(() => {
    if (accessLoading || !canViewPedagogySection) return
    void loadTeacher()
  }, [accessLoading, canViewPedagogySection, loadTeacher])

  /**
   * Справочник франчайзи для связи (115 строк на всю сеть) грузится один раз на
   * карточку и уходит в панель — иначе шторка «Добавить связь» тянула бы его
   * повторно. Отказ гасим: он решает только видимость кнопки, а не карточку.
   */
  useEffect(() => {
    if (accessLoading || !canViewPedagogySection || isCreateRoute) return
    let cancelled = false
    listTeacherRegistryReferenceData()
      .then(reference => { if (!cancelled) setAssignmentReference(reference) })
      .catch(referenceError => console.error('Failed to load assignment options:', referenceError))
    return () => { cancelled = true }
  }, [accessLoading, canViewPedagogySection, isCreateRoute])
  const openEdit = () => {
    if (!teacher) return
    setDraft({
      fullName: teacher.fullName,
      phoneNumber: teacher.phoneNumber,
      email: teacher.email,
      telegramUsername: teacher.telegramUsername,
      qualification: teacher.qualification ?? '',
    })
    setSaveError('')
    setEditOpen(true)
  }

  const save = async () => {
    if (!teacher) return
    setSaving(true)
    setSaveError('')
    try {
      await updateFranchiseTeacher(teacher.teacherId, {
        fullName: draft.fullName,
        phoneNumber: draft.phoneNumber,
        email: draft.email,
        telegramUsername: normalizeTelegramUsername(draft.telegramUsername),
        qualification: draft.qualification || null,
        comment: teacher.comment,
      })
      setEditOpen(false)
      await loadTeacher({ silent: true })
    } catch (saveFailure) {
      setSaveError(saveFailure instanceof Error ? saveFailure.message : 'Не удалось сохранить карточку')
    } finally {
      setSaving(false)
    }
  }

  const openCommentEditor = () => {
    if (!teacher) return
    setCommentDraft(teacher.comment)
    setSaveError('')
    setCommentOpen(true)
  }

  const saveComment = async (nextComment = commentDraft) => {
    if (!teacher) return
    setSaving(true)
    setSaveError('')
    try {
      await updateFranchiseTeacher(teacher.teacherId, {
        fullName: teacher.fullName,
        phoneNumber: teacher.phoneNumber,
        email: teacher.email,
        telegramUsername: normalizeTelegramUsername(teacher.telegramUsername),
        qualification: teacher.qualification,
        comment: nextComment.trim(),
      })
      setCommentOpen(false)
      await loadTeacher({ silent: true })
    } catch (saveFailure) {
      setSaveError(saveFailure instanceof Error ? saveFailure.message : 'Не удалось сохранить комментарий')
    } finally {
      setSaving(false)
    }
  }

  const clearComment = async () => {
    if (!teacher?.comment) return
    if (!window.confirm('Удалить рабочий комментарий?')) return
    await saveComment('')
  }

  const openArchiveSheet = (mode: 'archive' | 'restore') => {
    if (!teacher || !canManagePedagogy) return
    setArchiveReason('')
    setSaveError('')
    setArchiveMode(mode)
  }

  const submitArchiveChange = async () => {
    if (!teacher || !canManagePedagogy || !archiveMode) return
    const reason = archiveReason.trim()
    if (!reason) {
      setSaveError(archiveMode === 'archive' ? 'Укажите причину архивации' : 'Укажите причину восстановления')
      return
    }

    setSaving(true)
    setSaveError('')
    try {
      if (archiveMode === 'archive') await archiveFranchiseTeacher(teacher.teacherId, reason)
      else await restoreArchivedFranchiseTeacher(teacher.teacherId, reason)
      setArchiveMode(null)
      setArchiveReason('')
      await loadTeacher({ silent: true })
    } catch (failure) {
      // Лист остаётся открытым: ошибка должна быть видна рядом с действием,
      // а введённая причина — не потеряна.
      setSaveError(failure instanceof Error
        ? failure.message
        : archiveMode === 'archive' ? 'Не удалось архивировать карточку' : 'Не удалось восстановить карточку')
    } finally {
      setSaving(false)
    }
  }

  const openVideo = () => {
    setEditingVideoId(null)
    setEvaluateVideo(false)
    setVideoDraft({
      videoUrl: '', charismaScore: '', professionalismScore: '', clarityScore: '', emotionalityScore: '',
      decision: '', comment: '',
    })
    setSaveError('')
    setVideoOpen(true)
  }

  const openVideoDecisionOverride = (video: TeacherVideoPresentation) => {
    setVideoDecisionOverrideDraft({
      videoPresentationId: video.videoPresentationId,
      decision: video.decision ?? '',
      reason: '',
    })
    setSaveError('')
    setVideoDecisionOverrideOpen(true)
  }

  const saveVideoDecisionOverride = async () => {
    if (!videoDecisionOverrideDraft.videoPresentationId || !videoDecisionOverrideDraft.reason.trim()) {
      setSaveError('Укажите причину изменения решения')
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      await overrideUsedTeacherVideoDecision(
        videoDecisionOverrideDraft.videoPresentationId,
        videoDecisionOverrideDraft.decision || null,
        videoDecisionOverrideDraft.reason
      )
      setVideoDecisionOverrideOpen(false)
      await loadTeacher({ silent: true })
    } catch (failure) {
      setSaveError(failure instanceof Error ? failure.message : 'Не удалось изменить решение по видеовизитке')
    } finally {
      setSaving(false)
    }
  }

  const openExistingVideo = (video: TeacherVideoPresentation) => {
    setEditingVideoId(video.videoPresentationId)
    setEvaluateVideo(true)
    setVideoDraft({
      videoUrl: video.videoUrl,
      charismaScore: video.charismaScore == null ? '' : String(video.charismaScore),
      professionalismScore: video.professionalismScore == null ? '' : String(video.professionalismScore),
      clarityScore: video.clarityScore == null ? '' : String(video.clarityScore),
      emotionalityScore: video.emotionalityScore == null ? '' : String(video.emotionalityScore),
      decision: video.decision ?? '',
      comment: video.comment,
    })
    setSaveError('')
    setVideoOpen(true)
  }

  const saveVideo = async () => {
    if (!teacher || !/^https?:\/\//i.test(videoDraft.videoUrl.trim())) {
      setSaveError('Укажите корректную ссылку на видеовизитку')
      return
    }
    const scores = [
      Number(videoDraft.charismaScore),
      Number(videoDraft.professionalismScore),
      Number(videoDraft.clarityScore),
      Number(videoDraft.emotionalityScore),
    ]
    if (evaluateVideo) {
      if (!videoDraft.decision) {
        setSaveError('Укажите решение по видеовизитке')
        return
      }
      if (scores.some(value => !Number.isFinite(value) || value < 1 || value > 5)) {
        setSaveError('Все оценки видеовизитки должны быть от 1 до 5')
        return
      }
    }
    setSaving(true)
    setSaveError('')
    try {
      const payload = {
        videoUrl: videoDraft.videoUrl,
        charismaScore: evaluateVideo ? Number(videoDraft.charismaScore) : null,
        professionalismScore: evaluateVideo ? Number(videoDraft.professionalismScore) : null,
        clarityScore: evaluateVideo ? Number(videoDraft.clarityScore) : null,
        emotionalityScore: evaluateVideo ? Number(videoDraft.emotionalityScore) : null,
        decision: evaluateVideo ? videoDraft.decision || null : null,
        comment: videoDraft.comment,
      }
      if (editingVideoId) {
        await updateTeacherVideoPresentation(editingVideoId, payload)
      } else {
        await addTeacherVideoPresentation(teacher.teacherId, payload)
      }
      setVideoOpen(false)
      setEditingVideoId(null)
      await loadTeacher({ silent: true })
    } catch (failure) {
      setSaveError(failure instanceof Error ? failure.message : 'Не удалось сохранить видеовизитку')
    } finally {
      setSaving(false)
    }
  }

  const openAdditional = async () => {
    setEditingAdditionalId(null)
    setAdditionalDraft({
      attestationType: 'planned_recertification', directionId: '', attestationDate: localIsoDate(), result: '', comment: '',
    })
    setSaveError('')
    setAdditionalOpen(true)
    if (directionOptions.length === 0) {
      try {
        setDirectionOptions(await listTrainerReviewDirections())
      } catch (failure) {
        setSaveError(failure instanceof Error ? failure.message : 'Не удалось загрузить направления')
      }
    }
  }

  const openExistingAdditional = async (attestation: TeacherAdditionalAttestation) => {
    setEditingAdditionalId(attestation.additionalAttestationId)
    setAdditionalDraft({
      attestationType: attestation.attestationType,
      directionId: attestation.directionId == null ? '' : String(attestation.directionId),
      attestationDate: attestation.attestationDate,
      result: attestation.result ?? '',
      comment: attestation.comment,
    })
    setSaveError('')
    setAdditionalOpen(true)
    if (directionOptions.length === 0) {
      try {
        setDirectionOptions(await listTrainerReviewDirections())
      } catch (failure) {
        setSaveError(failure instanceof Error ? failure.message : 'Не удалось загрузить направления')
      }
    }
  }

  const saveAdditional = async () => {
    if (!teacher) return
    if (!additionalDraft.attestationDate) {
      setSaveError('Укажите дату аттестации')
      return
    }
    if (additionalDraft.attestationType === 'regulation' && !additionalDraft.directionId) {
      setSaveError('Выберите направление для аттестации по регламенту')
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      const payload = {
        attestationType: additionalDraft.attestationType,
        directionId: additionalDraft.directionId ? Number(additionalDraft.directionId) : null,
        attestationDate: additionalDraft.attestationDate,
        result: additionalDraft.result || null,
        comment: additionalDraft.comment,
      }
      if (editingAdditionalId) {
        await updateTeacherAdditionalAttestation(editingAdditionalId, payload)
      } else {
        await addTeacherAdditionalAttestation(teacher.teacherId, payload)
      }
      setAdditionalOpen(false)
      setEditingAdditionalId(null)
      await loadTeacher({ silent: true })
    } catch (failure) {
      setSaveError(failure instanceof Error ? failure.message : 'Не удалось сохранить дополнительную аттестацию')
    } finally {
      setSaving(false)
    }
  }

  const openExistingContract = (contract: TeacherContract) => {
    setEditingContractId(contract.contractId)
    setContractDraft({
      // Пришли из группы франчайзи — это лучший контекст для старого договора,
      // который исторически остался без partner_number. Не заставляем человека
      // заново выбирать того же франчайзи из общего списка.
      partnerNumber: contract.partnerNumber == null ? (preferredPartnerNumber == null ? '' : String(preferredPartnerNumber)) : String(contract.partnerNumber),
      signedOn: contract.signedOn ?? '',
      contractUrl: contract.contractUrl ?? '',
    })
    setSaveError('')
    setContractOpen(true)
  }

  const saveExistingContract = async () => {
    if (!editingContractId) return
    if (!contractDraft.partnerNumber) {
      setSaveError('Выберите франчайзи для договора')
      return
    }
    if (contractDraft.contractUrl.trim() && !/^https?:\/\//i.test(contractDraft.contractUrl.trim())) {
      setSaveError('Укажите корректную ссылку на договор')
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      await updateFranchiseTeacherContract(editingContractId, {
        partnerNumber: Number(contractDraft.partnerNumber),
        signedOn: contractDraft.signedOn || null,
        contractUrl: contractDraft.contractUrl.trim() || null,
      })
      setContractOpen(false)
      setEditingContractId(null)
      await loadTeacher({ silent: true })
    } catch (failure) {
      setSaveError(failure instanceof Error ? failure.message : 'Не удалось сохранить договор')
    } finally {
      setSaving(false)
    }
  }

  const assignmentGroups = useMemo(() => {
    if (!teacher) return []
    const groups = new Map<number, { partnerNumber: number; partnerName: string; cities: string[]; startsOn: string | null; assignments: TeacherAssignment[] }>()
    for (const assignment of teacher.assignments) {
      const group = groups.get(assignment.partnerNumber) ?? {
        partnerNumber: assignment.partnerNumber,
        partnerName: assignment.partnerName,
        cities: [],
        startsOn: assignment.startsOn,
        assignments: [],
      }
      if (!group.cities.includes(assignment.cityName)) group.cities.push(assignment.cityName)
      if (assignment.startsOn && (!group.startsOn || assignment.startsOn < group.startsOn)) group.startsOn = assignment.startsOn
      group.assignments.push(assignment)
      groups.set(assignment.partnerNumber, group)
    }
    return Array.from(groups.values()).sort((a, b) => a.partnerName.localeCompare(b.partnerName, 'ru'))
  }, [teacher])

  const endedAssignmentGroups = useMemo(() => {
    if (!teacher) return []
    const activeKeys = new Set(teacher.assignments.map(item => `${item.partnerNumber}:${item.cityId}`))
    const groups = new Map<number, { partnerNumber: number; partnerName: string; cities: string[]; startsOn: string | null; endsOn: string | null }>()
    for (const assignment of teacher.assignmentsHistory) {
      if (!assignment.endsOn || activeKeys.has(`${assignment.partnerNumber}:${assignment.cityId}`)) continue
      const group = groups.get(assignment.partnerNumber) ?? {
        partnerNumber: assignment.partnerNumber,
        partnerName: assignment.partnerName,
        cities: [],
        startsOn: assignment.startsOn,
        endsOn: assignment.endsOn,
      }
      if (!group.cities.includes(assignment.cityName)) group.cities.push(assignment.cityName)
      if (assignment.startsOn && (!group.startsOn || assignment.startsOn < group.startsOn)) group.startsOn = assignment.startsOn
      if (assignment.endsOn && (!group.endsOn || assignment.endsOn > group.endsOn)) group.endsOn = assignment.endsOn
      groups.set(assignment.partnerNumber, group)
    }
    return Array.from(groups.values()).sort((a, b) => a.partnerName.localeCompare(b.partnerName, 'ru'))
  }, [teacher])

  const contractPartnerOptions = useMemo(() => {
    const options = new Map<number, { partnerNumber: number; partnerName: string; ended: boolean }>()
    for (const group of assignmentGroups) {
      options.set(group.partnerNumber, { partnerNumber: group.partnerNumber, partnerName: group.partnerName, ended: false })
    }
    for (const group of endedAssignmentGroups) {
      if (!options.has(group.partnerNumber)) {
        options.set(group.partnerNumber, { partnerNumber: group.partnerNumber, partnerName: group.partnerName, ended: true })
      }
    }
    return Array.from(options.values())
  }, [assignmentGroups, endedAssignmentGroups])

  const activeUnlinkedContracts = useMemo(
    () => teacher?.contracts.filter(contract => contract.partnerNumber == null && contract.status === 'active') ?? [],
    [teacher]
  )

  const historicalUnlinkedContracts = useMemo(
    () => teacher?.contracts.filter(contract => contract.partnerNumber == null && contract.status !== 'active') ?? [],
    [teacher]
  )

  const contractsByPartner = useMemo(() => {
    const groups = new Map<number, TeacherContract[]>()
    for (const contract of teacher?.contracts ?? []) {
      if (contract.partnerNumber == null) continue
      groups.set(contract.partnerNumber, [...(groups.get(contract.partnerNumber) ?? []), contract])
    }
    for (const contracts of groups.values()) {
      contracts.sort((left, right) => {
        const statusDiff = Number(right.status === 'active') - Number(left.status === 'active')
        if (statusDiff) return statusDiff
        return (right.signedOn ?? '').localeCompare(left.signedOn ?? '')
      })
    }
    return groups
  }, [teacher?.contracts])

  const managementReviews = useMemo(
    () => (teacher?.reviews ?? []).filter(review => review.reviewSource === 'management_company'),
    [teacher?.reviews]
  )
  const seniorReviews = useMemo(
    () => (teacher?.reviews ?? []).filter(review => review.reviewSource === 'senior_teacher'),
    [teacher?.reviews]
  )
  const latestReviews = useMemo(() => {
    return [...managementReviews].sort((left, right) => right.reviewDate.localeCompare(left.reviewDate)).slice(0, 3)
  }, [managementReviews])
  const latestSeniorReviews = useMemo(() => {
    return [...seniorReviews].sort((left, right) => right.reviewDate.localeCompare(left.reviewDate)).slice(0, 3)
  }, [seniorReviews])

  const latestThreeQuality = useMemo(() => {
    if (latestReviews.length === 0) return null
    return latestReviews.reduce((sum, review) => sum + review.qualityPercent, 0) / latestReviews.length
  }, [latestReviews])

  const allTimeQuality = useMemo(() => {
    if (teacher?.averageQualityPercent != null) return teacher.averageQualityPercent
    if (managementReviews.length === 0) return null
    return managementReviews.reduce((sum, review) => sum + review.qualityPercent, 0) / managementReviews.length
  }, [managementReviews, teacher?.averageQualityPercent])

  const seniorLatestThreeQuality = useMemo(() => {
    if (latestSeniorReviews.length === 0) return null
    return latestSeniorReviews.reduce((sum, review) => sum + review.qualityPercent, 0) / latestSeniorReviews.length
  }, [latestSeniorReviews])

  const seniorAllTimeQuality = useMemo(() => {
    if (seniorReviews.length === 0) return null
    return seniorReviews.reduce((sum, review) => sum + review.qualityPercent, 0) / seniorReviews.length
  }, [seniorReviews])

  const attentionItems = useMemo(() => teacher ? getTeacherInlineAttention(teacher) : [], [teacher])
  const profileAttentionItems = useMemo(
    () => attentionItems.filter(item => !item.key.startsWith('attestation-') && !item.key.startsWith('training-')),
    [attentionItems]
  )

  if (isCreateRoute) return <PedagogyTeacherCreatePage />

  if (accessError) return <PedagogyAccessErrorCard onRetry={retryAccessCheck} />
  if (!accessLoading && !canViewPedagogySection) {
    return (
      <div className="space-y-4 pb-24">
        <Card>
          <div className="text-sm text-muted-foreground">
            Раздел педагогов доступен специалистам педагогического отдела, руководителю педагогов и пользователям с уровнем advanced.
          </div>
        </Card>
      </div>
    )
  }

  if (accessLoading || loading) {
    return (
      <div className="space-y-4 pb-24">
        <Card><div className="py-10 text-center text-sm text-muted-foreground">Загружаем карточку…</div></Card>
      </div>
    )
  }

  if (!teacher) {
    return (
      <div className="space-y-4 pb-24">
        <PedagogyBackLink to={backToTeachers} label="К педагогам" />
        <Card className="border border-error/30 bg-error/5">
          <div className="flex items-start gap-2 text-error"><AlertTriangle size={18} />{error || 'Карточка не найдена'}</div>
        </Card>
      </div>
    )
  }

  const currentTraining = teacher.currentTraining
  const stage2Enabled = currentTraining?.firstAttestationResult === 'passed'
  const stage3Enabled = currentTraining?.secondAttestationResult === 'passed'
  const currentTrainingStage = currentTraining ? trainingStageLabel(currentTraining) : null
  const trainingOverdueDays = currentTraining?.status === 'in_progress' ? daysOverdue(currentTraining.deadlineDate) : 0
  const nextTrainingAction = currentTraining ? (() => {
    const stages = [
      { stage: 1 as const, ordinal: '1-ю', date: currentTraining.firstAttestationDate, result: currentTraining.firstAttestationResult, enabled: true },
      { stage: 2 as const, ordinal: '2-ю', date: currentTraining.secondAttestationDate, result: currentTraining.secondAttestationResult, enabled: stage2Enabled },
      { stage: 3 as const, ordinal: '3-ю', date: currentTraining.thirdAttestationDate, result: currentTraining.thirdAttestationResult, enabled: stage3Enabled },
    ]
    const item = stages.find(stage => stage.enabled && stage.result !== 'passed')
    if (!item) return null
    if (item.result === 'refused') return null
    if (item.result === 'retake') return { stage: item.stage, label: `Назначить пересдачу ${item.ordinal} аттестации`, button: 'Назначить пересдачу' }
    if (item.date) return { stage: item.stage, label: `Зафиксировать результат ${item.ordinal} аттестации`, button: 'Зафиксировать результат' }
    return { stage: item.stage, label: `Запланировать ${item.ordinal} аттестацию`, button: 'Запланировать' }
  })() : null
  const primaryAttention = attentionItems[0] ?? null
  const primaryAttentionIsTraining = Boolean(primaryAttention && (primaryAttention.key.startsWith('attestation-') || primaryAttention.key.startsWith('training-')))
  const legacyTrainingNeedsReview = Boolean(currentTraining?.isLegacyImport && currentTraining.status === 'in_progress' && trainingOverdueDays > 0)
  const headerAttentionLabel = primaryAttentionIsTraining && legacyTrainingNeedsReview
    ? 'Исторический цикл обучения требует сверки'
    : primaryAttention?.label
  const headerAttentionTone = primaryAttentionIsTraining && legacyTrainingNeedsReview ? 'warning' : primaryAttention?.tone
  const canCreateReview = canEditTeacher && teacher.status === 'working'
  const confirmManualTeacher = async () => {
    try {
      const pendingAssignments = teacher.assignments.filter(assignment => assignment.needsPedagogyReview)
      if (pendingAssignments.length > 0) {
        await Promise.all(pendingAssignments.map(assignment => confirmTeacherFranchiseAssignment(assignment.assignmentId)))
      } else {
        await confirmTeacherRegistryCard(teacher.teacherId)
      }
      await loadTeacher({ silent: true })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось подтвердить карточку педагога')
    }
  }
  const createReviewUrl = `${pedagogyPaths.reviewNew}?teacherId=${encodeURIComponent(teacher.teacherId)}&returnTo=${encodeURIComponent(window.location.pathname)}`
  const allReviewsUrl = `${pedagogyPaths.reviews}?teacherId=${encodeURIComponent(teacher.teacherId)}&teacherName=${encodeURIComponent(teacher.fullName)}&returnTo=${encodeURIComponent(window.location.pathname)}`

  return (
    <div className="space-y-4 pb-24">
      <Card className="px-3 py-2.5 md:px-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1 space-y-1.5">
            <button
              type="button"
              onClick={() => navigate(backToTeachers)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              aria-label="К педагогам"
              title="К педагогам"
            >
              <ArrowLeft size={14} />
              <span>К педагогам</span>
            </button>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h1 className="text-lg font-semibold leading-tight md:text-xl">{teacher.fullName}</h1>
              <span className="text-sm text-muted-foreground">· {teacher.qualification ? qualificationLabels[teacher.qualification] : 'Квалификация не указана'}</span>
              <TeacherStatusBadge status={teacher.status} />
              {teacher.assignments.some(assignment => assignment.isSenior) && (
                <Badge variant="warning" className="px-2 py-0.5 text-xs">Старший педагог</Badge>
              )}
              {teacher.needsPedagogyReview && teacher.assignments.every(assignment => !assignment.needsPedagogyReview) && (
                <Badge variant="info" className="px-2 py-0.5 text-xs">Проверьте карточку педагога</Badge>
              )}
              {currentTraining?.isRepeatTraining && <Badge variant="secondary">Повторное обучение</Badge>}
            </div>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Phone size={14} />{teacher.phoneNumber || 'Телефон не указан'}</span>
              <span className="text-border">·</span>
              <span className="inline-flex items-center gap-1"><Mail size={14} />{teacher.email || 'Email не указан'}</span>
              <span className="text-border">·</span>
              <span className="inline-flex items-center gap-1">
                <MessageCircle size={14} />
                {teacher.telegramUsername ? (
                  <a href={`https://t.me/${normalizeTelegramUsername(teacher.telegramUsername)}`} target="_blank" rel="noreferrer" className="hover:text-foreground hover:underline">
                    {formatTelegramUsername(teacher.telegramUsername)}
                  </a>
                ) : 'Telegram не указан'}
              </span>
            </div>

            {(primaryAttention || teacher.comment || canEditTeacher) && (
              <div className="flex flex-wrap items-start gap-x-3 gap-y-1.5 pt-0.5">
                {primaryAttention && headerAttentionTone && !(activeTab === 'training' && primaryAttentionIsTraining) && (
                  <div className={`inline-flex max-w-full items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs ${teacherAttentionClass(headerAttentionTone)}`}>
                    <AlertTriangle size={13} className="shrink-0" />
                    <span className="truncate">{headerAttentionLabel}</span>
                  </div>
                )}

                {teacher.comment ? (
                  <div className="flex min-w-0 max-w-3xl items-start gap-1.5 text-sm text-muted-foreground">
                    <MessageCircle size={14} className="mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <span
                        className={`whitespace-pre-wrap leading-5 text-foreground ${commentNeedsExpansion && !commentExpanded ? 'inline-block max-h-10 overflow-hidden align-top' : ''}`}
                      >
                        {teacher.comment}
                      </span>
                      <span className="ml-2 inline-flex items-center gap-2 whitespace-nowrap text-xs font-medium">
                        {commentNeedsExpansion && (
                          <button type="button" className="text-primary" onClick={() => setCommentExpanded(value => !value)}>
                            {commentExpanded ? 'Свернуть' : 'Ещё'}
                          </button>
                        )}
                        {canEditTeacher && (
                          <button type="button" className="inline-flex items-center gap-1 text-primary" onClick={openCommentEditor} title="Изменить комментарий">
                            <Pencil size={12} />
                            <span className="hidden sm:inline">Изменить</span>
                          </button>
                        )}
                      </span>
                    </div>
                  </div>
                ) : canEditTeacher ? (
                  <button type="button" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary" onClick={openCommentEditor}>
                    <MessageCircle size={13} />
                    + комментарий
                  </button>
                ) : null}
              </div>
            )}
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2 md:pt-0.5">
            <Button size="sm" variant="outline" onClick={openEdit} disabled={!canEditTeacher}>
              <Pencil size={16} className="mr-1.5 inline" />
              Изменить
            </Button>
            {canManagePedagogy && (
              <div ref={archiveMenuRef} className="relative" onKeyDown={event => { if (event.key === 'Escape') setArchiveMenuOpen(false) }}>
                <button
                  type="button"
                  onClick={() => setArchiveMenuOpen(value => !value)}
                  aria-expanded={archiveMenuOpen}
                  aria-haspopup="menu"
                  aria-label="Действия с карточкой"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted"
                >
                  <MoreVertical size={17} />
                </button>
                {archiveMenuOpen && (
                  <div role="menu" className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-border bg-card p-1 shadow-lg">
                  {teacher.archivedAt ? (
                    <button type="button" role="menuitem" className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => { setArchiveMenuOpen(false); openArchiveSheet('restore') }} disabled={saving}>
                      <ArchiveRestore size={15} />Восстановить
                    </button>
                  ) : (
                    <button type="button" role="menuitem" className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => { setArchiveMenuOpen(false); openArchiveSheet('archive') }} disabled={saving}>
                      <Archive size={15} />В архив
                    </button>
                  )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {(teacher.needsPedagogyReview || teacher.assignments.some(assignment => assignment.needsPedagogyReview)) && canManagePedagogy && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <span className="flex-1">{teacher.assignments.some(assignment => assignment.needsPedagogyReview) ? 'Связь с франчайзи создана вручную. Проверьте её актуальность.' : 'Карточка добавлена франчайзи и ожидает проверки педагогического отдела.'}</span>
            <Button size="sm" variant="outline" onClick={() => void confirmManualTeacher()}>Подтвердить карточку</Button>
          </div>
        )}
      </Card>

      {error && (
        <Card className="border border-error/30 bg-error/5">
          <div className="flex items-start gap-2 text-sm text-error"><AlertTriangle size={18} />{error}</div>
        </Card>
      )}

      {/* Страховка: `saveError` рендерится внутри листов, поэтому ошибка,
          выставленная действием без открытого листа, раньше уходила в никуда. */}
      {saveError && !anySheetOpen && (
        <Card className="border border-error/30 bg-error/5">
          <div className="flex items-start gap-2 text-sm text-error" role="alert">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <span>{saveError}</span>
          </div>
        </Card>
      )}

      {teacher.archivedAt && (
        <Card className="border border-amber-300 bg-amber-50">
          <div className="flex items-start gap-2 text-sm leading-6 text-amber-900">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            Карточка в архиве. Активные процессы при архивации не закрывались; для работы с педагогом сначала восстановите карточку.
          </div>
        </Card>
      )}

      <div className="overflow-x-auto rounded-xl border border-border bg-card p-1">
        <div className="flex min-w-max gap-1">
          {detailTabs.map(tab => {
            const Icon = tab.icon
            const active = activeTab === tab.value
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value)}
                className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${
                  active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      <main className="min-w-0 space-y-4">
          {activeTab === 'profile' && (
            <>
              <Card className="p-4">
                <SectionHeader
                  icon={Users}
                  title="Работа у франчайзи и договоры"
                  count={teacher.assignments.length}
                  action={canAddAssignment || canEditTeacher ? (
                    <div className="flex flex-wrap gap-2">
                      {canAddAssignment && (
                        <Button size="sm" variant="outline" onClick={() => lifecycleRef.current?.openAssignments()}>Добавить связь</Button>
                      )}
                      {canEditTeacher && (
                        <Button size="sm" onClick={() => lifecycleRef.current?.openContract(preferredPartnerNumber ?? undefined)} disabled={teacher.assignments.length === 0}>
                          <Plus size={15} className="mr-1.5 inline" />Добавить договор
                        </Button>
                      )}
                    </div>
                  ) : null}
                />
                {!canEditTeacher && !teacher.archivedAt && (
                  <div className="mb-3 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
                    Педагог не связан с вашими франчайзи, поэтому карточка открыта только для просмотра.
                    {canAddAssignment ? ' Добавьте связь со своим франчайзи, чтобы вести его карточку.' : ''}
                  </div>
                )}
                {activeUnlinkedContracts.length > 0 && (
                  <details className="mb-3 rounded-lg border border-amber-200/80 bg-amber-50/30 px-3 py-2">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm">
                      <span className="inline-flex min-w-0 items-center gap-2 font-medium text-foreground">
                        <AlertTriangle size={15} className="shrink-0 text-amber-600" />
                        {activeUnlinkedContracts.length} {pluralRu(activeUnlinkedContracts.length, 'договор требует', 'договора требуют', 'договоров требуют')} привязки
                      </span>
                      <span className="shrink-0 text-xs font-medium text-primary">Показать</span>
                    </summary>
                    <div className="mt-2 divide-y divide-border/70 border-t border-border/70">
                      {activeUnlinkedContracts.map(contract => (
                        <div key={contract.contractId} className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="success">действует</Badge>
                              <span className="text-sm">{contract.signedOn ? `Подписан ${formatDate(contract.signedOn)}` : 'Дата подписания неизвестна'}</span>
                              {contract.expiresOn && <span className="text-sm text-muted-foreground">· до {formatDate(contract.expiresOn)}</span>}
                            </div>
                            <div className="mt-1"><ContractExternalLink contract={contract} /></div>
                          </div>
                          {canEditTeacher && (
                            <button type="button" className="self-start text-sm font-medium text-primary sm:self-auto" onClick={() => openExistingContract(contract)}>
                              Привязать
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {historicalUnlinkedContracts.length > 0 && (
                  <details className="mb-3 rounded-lg border border-border/70 px-3 py-2">
                    <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                      Предыдущие договоры без привязки · {historicalUnlinkedContracts.length}
                    </summary>
                    <div className="mt-2 divide-y divide-border/70">
                      {historicalUnlinkedContracts.map(contract => (
                        <div key={contract.contractId} className="flex flex-col gap-2 py-2 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant={contractStatusVariant(contract)}>{contractStatusLabel(contract)}</Badge>
                              <span className="text-sm text-muted-foreground">{contract.signedOn ? `Подписан ${formatDate(contract.signedOn)}` : 'Дата подписания не указана'}</span>
                            </div>
                            <div className="mt-1"><ContractExternalLink contract={contract} /></div>
                          </div>
                          {canEditTeacher && (
                            <button type="button" className="self-start text-sm text-primary sm:self-auto" onClick={() => openExistingContract(contract)}>
                              Привязать
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {assignmentGroups.length === 0 ? (
                  <EmptyState text="Активные связи не указаны." actionLabel={canAddAssignment ? 'Добавить связь' : undefined} onAction={canAddAssignment ? () => lifecycleRef.current?.openAssignments() : undefined} />
                ) : (
                  <div className="space-y-2">
                    {assignmentGroups.map(group => {
                      const partnerContracts = contractsByPartner.get(group.partnerNumber) ?? []
                      const activePartnerContracts = partnerContracts.filter(contract => contract.status === 'active')
                      const previousPartnerContracts = partnerContracts.filter(contract => contract.status !== 'active')
                      return (
                      <div key={group.partnerNumber} className="rounded-lg border border-border p-3">
                        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                              <span>{group.partnerName}</span>
                              <span className="text-muted-foreground">·</span>
                              <span className="inline-flex items-center gap-1 text-muted-foreground"><MapPin size={13} />{group.cities.join(', ')}</span>
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">{group.startsOn ? `Работает с ${formatDate(group.startsOn)}` : 'Дата начала работы не указана'}</div>
                          </div>
                          {canEditTeacher && (
                            <div className="flex shrink-0 flex-wrap gap-2">
                              <Button size="sm" variant="outline" onClick={() => lifecycleRef.current?.openDismissPartner(group.partnerNumber)}>Уволить у франчайзи</Button>
                            </div>
                          )}
                        </div>
                        <div className="mt-3 space-y-2">
                          {group.assignments.length > 1 ? (
                            <div className="space-y-1 rounded-md bg-muted/30 px-3 py-2">
                              {group.assignments.map(assignment => (
                                <div key={assignment.assignmentId} className="flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:justify-between">
                                  <div className="min-w-0">
                                    <span className="font-medium">{assignment.cityName}</span>
                                    <span className="text-muted-foreground">{assignment.startsOn ? ` · с ${formatDate(assignment.startsOn)}` : ' · дата начала не указана'}</span>
                                  </div>
                                  {canEditTeacher && (
                                    <div className="flex shrink-0 items-center gap-3 self-start sm:self-auto">
                                      <button
                                        type="button"
                                        className="inline-flex items-center gap-1 text-xs text-primary"
                                        onClick={() => lifecycleRef.current?.openEditAssignment(assignment.assignmentId)}
                                      >
                                        <Pencil size={13} />
                                        Изменить
                                      </button>
                                      {canManagePedagogy && (
                                        <button
                                          type="button"
                                          className="inline-flex items-center gap-1 text-xs text-error"
                                          onClick={() => lifecycleRef.current?.openDeleteAssignment(assignment.assignmentId)}
                                        >
                                          <Trash2 size={13} />
                                          Удалить ошибочную связь
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : canEditTeacher ? (
                            <div className="flex justify-end">
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 text-xs text-primary"
                                  onClick={() => lifecycleRef.current?.openEditAssignment(group.assignments[0].assignmentId)}
                                >
                                  <Pencil size={13} />Изменить
                                </button>
                                {canManagePedagogy && (
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 text-xs text-error"
                                    onClick={() => lifecycleRef.current?.openDeleteAssignment(group.assignments[0].assignmentId)}
                                  >
                                    <Trash2 size={13} />
                                    Удалить ошибочную связь
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : null}
                          {activePartnerContracts.map(contract => (
                            <div key={contract.contractId} className="flex flex-col gap-2 rounded-md bg-muted/35 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge variant={contractStatusVariant(contract)}>{contractStatusLabel(contract)}</Badge>
                                  <span className="text-sm">{contract.signedOn ? `Подписан ${formatDate(contract.signedOn)}` : 'Дата подписания не указана'}</span>
                                  {contract.expiresOn && <span className="text-sm text-muted-foreground">· срок до {formatDate(contract.expiresOn)}</span>}
                                </div>
                                <div className="mt-1"><ContractExternalLink contract={contract} /></div>
                              </div>
                              {canEditTeacher && (
                                <div className="flex flex-wrap items-center gap-3 text-sm">
                                  <button type="button" className="text-primary" onClick={() => openExistingContract(contract)}>Изменить</button>
                                  <button type="button" className="text-error" onClick={() => lifecycleRef.current?.terminateContract(contract.contractId)}>Расторгнуть</button>
                                </div>
                              )}
                            </div>
                          ))}
                          {previousPartnerContracts.length > 0 && (
                            <details className="rounded-md border border-border/70 px-3 py-2">
                              <summary className="cursor-pointer text-sm font-medium text-primary">
                                Предыдущие договоры · {previousPartnerContracts.length}
                              </summary>
                              <div className="mt-2 space-y-2">
                                {previousPartnerContracts.map(contract => (
                                  <div key={contract.contractId} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                                    <span>{contractStatusLabel(contract)} · {contract.signedOn ? `подписан ${formatDate(contract.signedOn)}` : 'дата подписания не указана'}{contract.expiresOn ? ` · срок до ${formatDate(contract.expiresOn)}` : ''}</span>
                                    <span className="flex flex-wrap gap-2">
                                      <ContractExternalLink contract={contract} />
                                      {canEditTeacher && <button type="button" className="text-primary" onClick={() => openExistingContract(contract)}>Изменить</button>}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      </div>
                    )})}
                  </div>
                )}
                {endedAssignmentGroups.length > 0 && (
                  <details className="mt-3 rounded-lg border border-border/70 px-3 py-2">
                    <summary className="cursor-pointer text-sm font-medium text-primary">Предыдущие места работы · {endedAssignmentGroups.length}</summary>
                    <div className="mt-2 divide-y divide-border/70">
                      {endedAssignmentGroups.map(group => (
                        <div key={group.partnerNumber} className="py-2 text-sm">
                          <div className="font-medium">{group.partnerName} · {group.cities.join(', ')}</div>
                          <div className="text-xs text-muted-foreground">{group.startsOn ? formatDate(group.startsOn) : 'Дата начала не указана'} — {group.endsOn ? formatDate(group.endsOn) : 'дата окончания не указана'}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </Card>

              <Card className="p-4">
                <SectionHeader icon={ClipboardCheck} title="Качество и отсмотры" />
                {profileAttentionItems.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {profileAttentionItems.map(item => (
                      <span key={item.key} className={`inline-flex rounded-lg border px-2.5 py-1 text-xs ${teacherAttentionClass(item.tone)}`}>{item.label}</span>
                    ))}
                  </div>
                )}
                {teacher.reviews.length === 0 ? (
                  <div className="flex flex-col gap-3 rounded-lg border border-border px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-sm font-medium">Отсмотров пока нет</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">Качество появится после первого отсмотра.</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {canCreateReview && (
                        <Button size="sm" onClick={() => navigate(createReviewUrl)}>
                          <Plus size={15} className="mr-1.5 inline" />Создать отсмотр
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => navigate(allReviewsUrl)}>
                        {canManagePedagogy ? 'Все отсмотры' : isPedagogySpecialist ? 'Доступные отсмотры' : 'Мои отсмотры'} <ExternalLink size={14} className="ml-1.5 inline" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">УК</div>
                    <div className="grid overflow-hidden rounded-lg border border-border sm:grid-cols-2 xl:grid-cols-4">
                      <div className="border-b border-border px-3 py-2.5 sm:border-r xl:border-b-0">
                        <div className="text-xs text-muted-foreground">Среднее последних 3</div>
                        <div className="mt-1 text-lg font-semibold">{formatQuality(latestThreeQuality)}</div>
                      </div>
                      <div className="border-b border-border px-3 py-2.5 xl:border-b-0 xl:border-r">
                        <div className="text-xs text-muted-foreground">Среднее за всё время</div>
                        <div className="mt-1 text-lg font-semibold">{formatQuality(allTimeQuality)}</div>
                      </div>
                      <div className="border-b border-border px-3 py-2.5 sm:border-b-0 sm:border-r">
                        <div className="text-xs text-muted-foreground">Последний отсмотр</div>
                        <div className="mt-1 text-sm font-semibold">{formatDate(teacher.latestReviewDate)}</div>
                      </div>
                      <div className="px-3 py-2.5">
                        <div className="text-xs text-muted-foreground">Всего отсмотров</div>
                        <div className="mt-1 text-lg font-semibold">{teacher.reviewsCount}</div>
                      </div>
                    </div>
                    {latestReviews.length > 0 && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        Последние результаты: {latestReviews.map(review => formatQuality(review.qualityPercent)).join(' · ')}
                      </div>
                    )}
                    {seniorReviews.length > 0 && (
                      <div className="mt-4 border-t border-border pt-4">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Старший педагог</div>
                        <div className="grid overflow-hidden rounded-lg border border-border sm:grid-cols-2 xl:grid-cols-4">
                          <div className="border-b border-border px-3 py-2.5 sm:border-r xl:border-b-0">
                            <div className="text-xs text-muted-foreground">Среднее последних 3</div>
                            <div className="mt-1 text-lg font-semibold">{formatQuality(seniorLatestThreeQuality)}</div>
                          </div>
                          <div className="border-b border-border px-3 py-2.5 xl:border-b-0 xl:border-r">
                            <div className="text-xs text-muted-foreground">Среднее за всё время</div>
                            <div className="mt-1 text-lg font-semibold">{formatQuality(seniorAllTimeQuality)}</div>
                          </div>
                          <div className="border-b border-border px-3 py-2.5 sm:border-b-0 sm:border-r">
                            <div className="text-xs text-muted-foreground">Последний отсмотр</div>
                            <div className="mt-1 text-sm font-semibold">{formatDate(latestSeniorReviews[0]?.reviewDate ?? null)}</div>
                          </div>
                          <div className="px-3 py-2.5">
                            <div className="text-xs text-muted-foreground">Всего отсмотров</div>
                            <div className="mt-1 text-lg font-semibold">{seniorReviews.length}</div>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">
                          Последние результаты: {latestSeniorReviews.map(review => formatQuality(review.qualityPercent)).join(' · ')}
                        </div>
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {canCreateReview && (
                        <Button size="sm" onClick={() => navigate(createReviewUrl)}>
                          <Plus size={15} className="mr-1.5 inline" />Создать отсмотр
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => navigate(allReviewsUrl)}>
                        {canManagePedagogy ? 'Все отсмотры' : isPedagogySpecialist ? 'Доступные отсмотры' : 'Мои отсмотры'} <ExternalLink size={14} className="ml-1.5 inline" />
                      </Button>
                    </div>
                  </>
                )}
              </Card>

              {/*
                Пять форм раздела требуют обязательную причину (увольнение,
                удаление связи, архивация, восстановление, смена решения по
                визитке). После разбора вкладки «История» 20.08.2026 блок исчез
                с карточки, хотя `statusHistory` продолжал грузиться: прочитать
                введённую причину было нельзя нигде, и обязательное поле
                превратилось в ритуал.
              */}
              {teacher.statusHistory.length > 0 && (
                <details className="rounded-xl border border-border bg-card p-4">
                  <summary className="cursor-pointer text-sm font-semibold text-primary">
                    История статусов · {teacher.statusHistory.length}
                  </summary>
                  <div className="mt-3 divide-y divide-border/70">
                    {teacher.statusHistory.map(item => (
                      <div key={item.statusHistoryId} className="py-2 text-sm">
                        <div className="font-medium">
                          {item.fromStatus ? `${teacherStatusLabels[item.fromStatus]} → ` : ''}{teacherStatusLabels[item.toStatus]}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {item.changedAt ? formatDate(item.changedAt) : 'Дата не указана'}
                          {item.reason ? ` · ${item.reason}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}

          {activeTab === 'training' && (
            <>
              <Card className="p-4">
                <SectionHeader
                  icon={BookOpen}
                  title="Текущее обучение"
                  action={currentTraining && canEditTeacher ? (
                    <details className="relative">
                      <summary className="inline-flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted" aria-label="Действия с обучением">
                        <MoreVertical size={16} />
                      </summary>
                      <div className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-border bg-card p-1 shadow-lg">
                        <button
                          type="button"
                          className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-muted"
                          onClick={event => {
                            event.currentTarget.closest('details')?.removeAttribute('open')
                            lifecycleRef.current?.openCloseTraining()
                          }}
                        >
                          Закрыть обучение
                        </button>
                      </div>
                    </details>
                  ) : null}
                />
                {!currentTraining ? (
                  <div className="flex flex-col gap-3 rounded-lg border border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-sm font-medium">
                        {teacher.status === 'working' && teacher.trainingCycles.length === 0
                          ? 'Работает · история обучения в системе отсутствует'
                          : 'Активного обучения нет'}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {teacher.status === 'working'
                          ? 'При необходимости можно запустить повторное обучение без фиктивной истории.'
                          : 'Новый цикл запускается отдельным действием.'}
                      </div>
                    </div>
                    {canEditTeacher && teacher.status !== 'dismissed' && (
                      <Button size="sm" onClick={() => lifecycleRef.current?.openTraining()}>
                        <Plus size={15} className="mr-1.5 inline" />{teacher.status === 'working' ? 'Начать повторное обучение' : 'Начать обучение'}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-border px-3 py-3">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <TrainingStatusBadge status={currentTraining.status} />
                            {currentTraining.isRepeatTraining && <Badge variant="secondary">Повторное</Badge>}
                            {currentTraining.isLegacyImport && <Badge variant="neutral">Перенесено из старого учёта</Badge>}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                            <span><span className="text-muted-foreground">Старт:</span> <span className="font-medium">{formatDate(currentTraining.startDate)}</span></span>
                            <span><span className="text-muted-foreground">Дедлайн:</span> <span className="font-medium">{formatDate(currentTraining.deadlineDate)}</span></span>
                            <span><span className="text-muted-foreground">Этап:</span> <span className="font-medium">{currentTrainingStage}</span></span>
                          </div>
                        </div>
                        {legacyTrainingNeedsReview ? (
                          <div className="max-w-sm text-sm text-muted-foreground">Исторический цикл требует сверки незавершённых этапов.</div>
                        ) : trainingOverdueDays > 0 ? (
                          <div className="text-sm font-medium text-error">Просрочено на {trainingOverdueDays} дн.</div>
                        ) : null}
                      </div>

                      {nextTrainingAction && (
                        <div className={`mt-3 flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-center sm:justify-between ${trainingOverdueDays > 0 && !legacyTrainingNeedsReview ? 'border-error/25' : 'border-border'}`}>
                          <div>
                            <div className="text-xs text-muted-foreground">Следующее действие</div>
                            <div className="mt-0.5 text-sm font-medium">{nextTrainingAction.label}</div>
                          </div>
                          {canEditTeacher && (
                            <Button size="sm" onClick={() => lifecycleRef.current?.openAttestation(nextTrainingAction.stage)}>
                              {nextTrainingAction.button}
                            </Button>
                          )}
                        </div>
                      )}

                      <details className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
                        <summary className="cursor-pointer font-medium text-foreground">Регламент обучения</summary>
                        <div className="mt-2 leading-5">
                          +1 день — запись на 1-ю аттестацию · +3 дня — контроль договора · +10 дней — 2-я аттестация · +20 дней — 3-я аттестация · +25 дней — незавершённое обучение требует внимания.
                        </div>
                      </details>
                    </div>

                    <div>
                      <div className="mb-2 text-xs font-medium text-muted-foreground">Аттестации</div>
                      <div className="grid gap-3 lg:grid-cols-3">
                        <AttestationCard title="1-я аттестация" date={currentTraining.firstAttestationDate} result={currentTraining.firstAttestationResult} attempts={teacher.attestationAttempts.filter(item => item.trainingCycleId === currentTraining.trainingCycleId && item.stage === 1)} active={currentTrainingStage === '1-я аттестация'} canEdit={canEditTeacher} showAction={nextTrainingAction?.stage !== 1} onEdit={() => lifecycleRef.current?.openAttestation(1)} />
                        <AttestationCard title="2-я аттестация" date={currentTraining.secondAttestationDate} result={currentTraining.secondAttestationResult} attempts={teacher.attestationAttempts.filter(item => item.trainingCycleId === currentTraining.trainingCycleId && item.stage === 2)} active={currentTrainingStage === '2-я аттестация'} canEdit={canEditTeacher} disabled={!stage2Enabled} showAction={nextTrainingAction?.stage !== 2} onEdit={() => lifecycleRef.current?.openAttestation(2)} />
                        <AttestationCard title="3-я аттестация" date={currentTraining.thirdAttestationDate} result={currentTraining.thirdAttestationResult} attempts={teacher.attestationAttempts.filter(item => item.trainingCycleId === currentTraining.trainingCycleId && item.stage === 3)} active={currentTrainingStage === '3-я аттестация'} canEdit={canEditTeacher} disabled={!stage3Enabled} showAction={nextTrainingAction?.stage !== 3} onEdit={() => lifecycleRef.current?.openAttestation(3)} />
                      </div>
                    </div>
                  </div>
                )}
              </Card>

              <Card className="p-4">
                <section>
                  <SectionHeader icon={Video} title="Видеовизитки" count={teacher.videoPresentations.length} action={canEditTeacher && teacher.status !== 'dismissed' ? <Button size="sm" variant="outline" onClick={openVideo}><Plus size={15} className="mr-1.5 inline" />Добавить</Button> : null} />
                  {teacher.videoPresentations.length === 0 ? (
                    <div className="rounded-lg bg-muted/30 px-3 py-2 text-sm text-muted-foreground">Видеовизиток нет.</div>
                  ) : (
                    <div className="divide-y divide-border/70 rounded-lg border border-border/70 px-3">
                      {teacher.videoPresentations.map(video => (
                        <div key={video.videoPresentationId} className="py-2.5">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="truncate text-sm font-medium">{video.partnerName || video.cityName ? [video.partnerName, video.cityName].filter(Boolean).join(' · ') : 'Видеовизитка педагога'}</span>
                                <Badge variant={videoDecisionVariant(video.decision)}>{videoDecisionLabel(video.decision)}</Badge>
                                {usedVideoIds.has(video.videoPresentationId) && <Badge variant="secondary">Используется в обучении</Badge>}
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground">{video.evaluatedAt ? formatDate(video.evaluatedAt) : 'Дата оценки не указана'} · оценка {video.averageScore == null ? '—' : video.averageScore}</div>
                              {video.comment && <div className="mt-1.5 text-sm text-muted-foreground">{video.comment}</div>}
                            </div>
                            <div className="flex shrink-0 flex-wrap items-center gap-3 text-sm">
                              <a href={video.videoUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary">Открыть видео <ExternalLink size={13} /></a>
                              {canEditTeacher && teacher.status !== 'dismissed' && (
                                <button type="button" className="text-primary" onClick={() => openExistingVideo(video)}>{video.decision == null ? 'Оценить' : 'Изменить'}</button>
                              )}
                              {canManagePedagogy && usedVideoIds.has(video.videoPresentationId) && video.decision === 'approved' && (
                                <button type="button" className="text-primary" onClick={() => openVideoDecisionOverride(video)}>Изменить решение</button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <div className="my-4 border-t border-border" />

                <section>
                  <SectionHeader icon={Calendar} title="Дополнительные аттестации" count={teacher.additionalAttestations.length} action={canEditTeacher && teacher.status !== 'dismissed' ? <Button size="sm" variant="outline" onClick={() => void openAdditional()}><Plus size={15} className="mr-1.5 inline" />Добавить</Button> : null} />
                  {teacher.additionalAttestations.length === 0 ? (
                    <div className="rounded-lg bg-muted/30 px-3 py-2 text-sm text-muted-foreground">Дополнительных аттестаций нет.</div>
                  ) : (
                    <div className="divide-y divide-border/70 rounded-lg border border-border/70 px-3">
                      {teacher.additionalAttestations.map(attestation => (
                        <div key={attestation.additionalAttestationId} className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-medium">{additionalTypeLabels[attestation.attestationType]}</span>
                              <AttestationResultBadge result={attestation.result} />
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">{attestation.directionName ? `${attestation.directionName} · ` : ''}{attestation.attestationDate ? formatDate(attestation.attestationDate) : 'Дата не указана'}</div>
                            {attestation.comment && <div className="mt-1.5 text-sm text-muted-foreground">{attestation.comment}</div>}
                          </div>
                          {canEditTeacher && teacher.status !== 'dismissed' && (
                            <button type="button" className="shrink-0 text-sm text-primary" onClick={() => void openExistingAdditional(attestation)}>Изменить</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </Card>

              {teacher.trainingCycles.some(cycle => cycle.trainingCycleId !== currentTraining?.trainingCycleId) && (
                <details className="rounded-xl border border-border bg-card p-4">
                  <summary className="cursor-pointer text-sm font-semibold text-primary">
                    Предыдущие циклы обучения · {teacher.trainingCycles.filter(cycle => cycle.trainingCycleId !== currentTraining?.trainingCycleId).length}
                  </summary>
                  <div className="mt-3 space-y-3">
                    {teacher.trainingCycles
                      .filter(cycle => cycle.trainingCycleId !== currentTraining?.trainingCycleId)
                      .map(cycle => {
                        return (
                          <div key={cycle.trainingCycleId} className="rounded-lg border border-border p-3">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div>
                                <div className="text-sm font-medium">{cycle.isRepeatTraining ? 'Повторное обучение' : 'Обучение'}</div>
                                <div className="mt-1 text-xs text-muted-foreground">
                                  {formatDate(cycle.startDate)} — {trainingEndLabel(cycle)}
                                </div>
                              </div>
                              <TrainingStatusBadge status={cycle.status} />
                            </div>
                            <div className="mt-3 grid gap-2 md:grid-cols-3">
                              <TrainingHistoryAttestation title="1-я аттестация" date={cycle.firstAttestationDate} result={cycle.firstAttestationResult} attempts={teacher.attestationAttempts.filter(item => item.trainingCycleId === cycle.trainingCycleId && item.stage === 1)} />
                              <TrainingHistoryAttestation title="2-я аттестация" date={cycle.secondAttestationDate} result={cycle.secondAttestationResult} attempts={teacher.attestationAttempts.filter(item => item.trainingCycleId === cycle.trainingCycleId && item.stage === 2)} />
                              <TrainingHistoryAttestation title="3-я аттестация" date={cycle.thirdAttestationDate} result={cycle.thirdAttestationResult} attempts={teacher.attestationAttempts.filter(item => item.trainingCycleId === cycle.trainingCycleId && item.stage === 3)} />
                            </div>
                            {cycle.closeReason && <div className="mt-2 text-sm text-muted-foreground">{cycle.closeReason}</div>}
                          </div>
                        )
                      })}
                  </div>
                </details>
              )}
            </>
          )}

        </main>

      <TeacherLifecyclePanel ref={lifecycleRef} teacher={teacher} onChanged={() => loadTeacher({ silent: true })} compact showHistory={false} showPanel={false} referenceData={assignmentReference} />

      <BottomSheet
        isOpen={archiveMode != null}
        onClose={() => { setArchiveMode(null); setSaveError('') }}
        title={archiveMode === 'restore' ? 'Восстановить карточку из архива' : 'Убрать карточку в архив'}
      >
        <div className="space-y-4 p-4">
          {saveError && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{saveError}</div>}
          <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm leading-6 text-muted-foreground">
            {archiveMode === 'restore'
              ? 'Карточка вернётся в работу. Причина сохранится в истории статусов.'
              : 'История сохранится. Активные связи, договоры и обучение автоматически не закрываются — их нужно закрыть отдельно.'}
          </div>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Причина *</span>
            <textarea
              className="input min-h-[92px] w-full"
              value={archiveReason}
              onChange={event => setArchiveReason(event.target.value)}
              placeholder={archiveMode === 'restore' ? 'Например: карточка убрана в архив по ошибке' : 'Например: педагог не выходит на связь более 3 месяцев'}
              autoFocus
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => void submitArchiveChange()} disabled={saving || !archiveReason.trim()}>
              {saving ? 'Сохраняем…' : archiveMode === 'restore' ? 'Восстановить' : 'Убрать в архив'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setArchiveMode(null); setSaveError('') }} disabled={saving}>
              Отмена
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={videoOpen} onClose={() => setVideoOpen(false)} title={editingVideoId ? 'Оценка видеовизитки' : 'Новая видеовизитка'}>
        <div className="space-y-4 p-4">
          {saveError && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{saveError}</div>}
          <label className="block space-y-1"><span className="text-sm font-medium">Ссылка на видео *</span><input className="input w-full" value={videoDraft.videoUrl} onChange={event => setVideoDraft(current => ({ ...current, videoUrl: event.target.value }))} placeholder="https://..." /></label>
          {!editingVideoId && (
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={evaluateVideo} onChange={event => setEvaluateVideo(event.target.checked)} />
              Оценить сразу
            </label>
          )}
          {evaluateVideo && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {([
                  ['charismaScore', 'Харизма'], ['professionalismScore', 'Профессионализм'],
                  ['clarityScore', 'Понятность'], ['emotionalityScore', 'Эмоциональность'],
                ] as const).map(([key, label]) => (
                  <label key={key} className="block space-y-1">
                    <span className="text-sm font-medium">{label}</span>
                    <select className="input w-full" value={videoDraft[key]} onChange={event => setVideoDraft(current => ({ ...current, [key]: event.target.value }))}>
                      <option value="">Выберите оценку</option>
                      {[1,2,3,4,5].map(value => <option key={value} value={value}>{value}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Решение</span>
                <select className="input w-full" value={videoDraft.decision} disabled={editingVideoIsUsed} onChange={event => setVideoDraft(current => ({ ...current, decision: event.target.value as VideoDecisionDraft }))}>
                  <option value="">Выберите решение</option>
                  <option value="approved">Допущен</option>
                  <option value="rejected">Не допущен</option>
                </select>
                {editingVideoIsUsed && (
                  <span className="text-xs leading-5 text-muted-foreground">
                    Эта визитка уже использована для запуска обучения. Ссылку, оценки и комментарий можно исправить здесь; решение меняется отдельным действием руководителя/advanced с обязательной причиной.
                  </span>
                )}
              </label>
            </>
          )}
          <label className="block space-y-1"><span className="text-sm font-medium">Комментарий</span><textarea className="input min-h-24 w-full resize-y" value={videoDraft.comment} onChange={event => setVideoDraft(current => ({ ...current, comment: event.target.value }))} /></label>
          <div className="flex justify-end gap-2 border-t border-border pt-3"><Button variant="outline" onClick={() => setVideoOpen(false)} disabled={saving}>Отмена</Button><Button onClick={() => void saveVideo()} disabled={saving || !videoDraft.videoUrl.trim()}>{saving ? 'Сохраняем…' : editingVideoId ? 'Сохранить оценку' : 'Сохранить'}</Button></div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={videoDecisionOverrideOpen} onClose={() => setVideoDecisionOverrideOpen(false)} title="Изменить решение по использованной визитке">
        <div className="space-y-4 p-4">
          {saveError && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{saveError}</div>}
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm leading-6 text-amber-900">
            Визитка уже была основанием для запуска обучения. Решение изменится в истории визитки, но активное обучение не будет закрыто автоматически.
          </div>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Новое решение</span>
            <select className="input w-full" value={videoDecisionOverrideDraft.decision} onChange={event => setVideoDecisionOverrideDraft(current => ({ ...current, decision: event.target.value as VideoDecisionDraft }))}>
              <option value="">Вернуть на оценку</option>
              <option value="approved">Допущен</option>
              <option value="rejected">Не допущен</option>
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Причина *</span>
            <textarea className="input min-h-24 w-full resize-y" value={videoDecisionOverrideDraft.reason} onChange={event => setVideoDecisionOverrideDraft(current => ({ ...current, reason: event.target.value }))} />
          </label>
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="outline" onClick={() => setVideoDecisionOverrideOpen(false)} disabled={saving}>Отмена</Button>
            <Button onClick={() => void saveVideoDecisionOverride()} disabled={saving || !videoDecisionOverrideDraft.reason.trim()}>
              {saving ? 'Сохраняем…' : 'Изменить решение'}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={additionalOpen} onClose={() => setAdditionalOpen(false)} title={editingAdditionalId ? 'Аттестация' : 'Дополнительная аттестация'}>
        <div className="space-y-4 p-4">
          {saveError && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{saveError}</div>}
          <label className="block space-y-1"><span className="text-sm font-medium">Тип</span><select className="input w-full" value={additionalDraft.attestationType} onChange={event => setAdditionalDraft(current => ({ ...current, attestationType: event.target.value as AdditionalAttestationType, directionId: event.target.value === 'regulation' ? current.directionId : '' }))}><option value="planned_recertification">Плановая переаттестация</option><option value="regulation">По регламенту</option><option value="low_quality">Из-за низкого качества</option></select></label>
          {additionalDraft.attestationType === 'regulation' && <label className="block space-y-1"><span className="text-sm font-medium">Направление *</span><select className="input w-full" value={additionalDraft.directionId} onChange={event => setAdditionalDraft(current => ({ ...current, directionId: event.target.value }))}><option value="">Выберите направление</option>{directionOptions.map(option => <option key={option.directionId} value={option.directionId}>{option.name}</option>)}</select></label>}
          <label className="block space-y-1"><span className="text-sm font-medium">Дата *</span><input type="date" className="input w-full" value={additionalDraft.attestationDate} onChange={event => setAdditionalDraft(current => ({ ...current, attestationDate: event.target.value }))} /></label>
          <label className="block space-y-1"><span className="text-sm font-medium">Результат</span><select className="input w-full" value={additionalDraft.result} onChange={event => setAdditionalDraft(current => ({ ...current, result: event.target.value as typeof current.result }))}><option value="">Не указан</option><option value="passed">Сдано</option><option value="retake">Пересдача</option><option value="refused">Отказ</option></select></label>
          <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">Дополнительная аттестация сохраняется в истории и не меняет статус педагога автоматически.</div>
          <label className="block space-y-1"><span className="text-sm font-medium">Комментарий</span><textarea className="input min-h-24 w-full resize-y" value={additionalDraft.comment} onChange={event => setAdditionalDraft(current => ({ ...current, comment: event.target.value }))} /></label>
          <div className="flex justify-end gap-2 border-t border-border pt-3"><Button variant="outline" onClick={() => setAdditionalOpen(false)} disabled={saving}>Отмена</Button><Button onClick={() => void saveAdditional()} disabled={saving || !additionalDraft.attestationDate}>{saving ? 'Сохраняем…' : editingAdditionalId ? 'Сохранить изменения' : 'Сохранить'}</Button></div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={contractOpen} onClose={() => setContractOpen(false)} title="Редактирование договора">
        <div className="space-y-4 p-4">
          {saveError && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{saveError}</div>}
          <label className="block space-y-1">
            <span className="text-sm font-medium">Франчайзи *</span>
            <select className="input w-full" value={contractDraft.partnerNumber} onChange={event => setContractDraft(current => ({ ...current, partnerNumber: event.target.value }))}>
              <option value="">Выберите франчайзи</option>
              {contractPartnerOptions.map(option => (
                <option key={option.partnerNumber} value={option.partnerNumber}>
                  {option.partnerName}{option.ended ? ' · завершённая связь' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Дата подписания</span>
            <input type="date" className="input w-full" value={contractDraft.signedOn} onChange={event => setContractDraft(current => ({ ...current, signedOn: event.target.value }))} />
            <span className="text-xs text-muted-foreground">Для legacy-договора дата может остаться неизвестной.</span>
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Ссылка на договор</span>
            <input className="input w-full" value={contractDraft.contractUrl} onChange={event => setContractDraft(current => ({ ...current, contractUrl: event.target.value }))} placeholder="https://..." />
            <span className="text-xs text-muted-foreground">Если у legacy-договора ссылка неизвестна, оставьте поле пустым.</span>
          </label>
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="outline" onClick={() => setContractOpen(false)} disabled={saving}>Отмена</Button>
            <Button onClick={() => void saveExistingContract()} disabled={saving || !contractDraft.partnerNumber}>
              {saving ? 'Сохраняем…' : 'Сохранить изменения'}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={commentOpen} onClose={() => setCommentOpen(false)} title="Рабочий комментарий">
        <div className="space-y-4 p-4">
          {saveError && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{saveError}</div>}
          <label className="block space-y-1">
            <span className="text-sm font-medium">Комментарий</span>
            <textarea
              className="input min-h-32 w-full resize-y"
              value={commentDraft}
              onChange={event => setCommentDraft(event.target.value)}
              placeholder="Актуальная рабочая информация по педагогу"
              autoFocus
            />
            <span className="text-xs text-muted-foreground">Здесь хранится один актуальный рабочий комментарий. Его можно изменить или удалить.</span>
          </label>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
            <div>
              {teacher.comment && (
                <button type="button" className="text-sm text-error" onClick={() => void clearComment()} disabled={saving}>
                  Удалить комментарий
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCommentOpen(false)} disabled={saving}>Отмена</Button>
              <Button onClick={() => void saveComment()} disabled={saving}>
                {saving ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={editOpen} onClose={() => setEditOpen(false)} title="Карточка педагога">
        <div className="space-y-4 p-4">
          {saveError && <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">{saveError}</div>}

          <label className="block space-y-1">
            <span className="text-sm font-medium">ФИО *</span>
            <input className="input w-full" value={draft.fullName} onChange={event => setDraft(current => ({ ...current, fullName: event.target.value }))} />
          </label>
          <label className="block space-y-1">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium"><Phone size={15} />Телефон</span>
            <input className="input w-full" type="tel" value={draft.phoneNumber} onChange={event => setDraft(current => ({ ...current, phoneNumber: event.target.value }))} />
          </label>
          <label className="block space-y-1">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium"><Mail size={15} />Email</span>
            <input className="input w-full" type="email" value={draft.email} onChange={event => setDraft(current => ({ ...current, email: event.target.value }))} />
          </label>
          <label className="block space-y-1">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium"><MessageCircle size={15} />Telegram</span>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">@</span>
              <input
                className="input w-full pl-7"
                value={normalizeTelegramUsername(draft.telegramUsername)}
                onChange={event => setDraft(current => ({ ...current, telegramUsername: normalizeTelegramUsername(event.target.value) }))}
                placeholder="username"
              />
            </div>
            <span className="text-xs text-muted-foreground">Введите только имя пользователя — @ уже добавляется интерфейсом.</span>
          </label>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Квалификация</span>
            <select className="input w-full" value={draft.qualification} onChange={event => setDraft(current => ({ ...current, qualification: event.target.value as '' | TeacherQualification }))}>
              <option value="">Не указана</option>
              <option value="stretch_pilates">Растяжка и пилатес</option>
              <option value="universal">Универсал</option>
            </select>
          </label>
          <div className="sticky bottom-0 flex justify-end gap-2 border-t border-border bg-background pt-3">
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>Отмена</Button>
            <Button onClick={save} disabled={saving || !draft.fullName.trim()}>
              <Save size={16} className="mr-1.5 inline" />{saving ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </div>
        </div>
      </BottomSheet>
    </div>
  )
}
